import React, { useState, useRef } from "react";

// 5-phase attentional funnel based on Attention Restoration Theory (Kaplan, 1995)
// and focused attention meditation research (Lutz et al., 2008)
const DEFAULT_PHASES = [
  {
    key:         "release",
    label:       "RELEASE",
    instruction: "What's pulling your attention right now? Name it — don't judge it.",
    hasInput:    true,
    inputLabel:  "What's distracting you?",
    placeholder: "The thing occupying your mind...",
  },
  {
    key:         "anchor",
    label:       "ANCHOR",
    instruction: "Place your attention on one physical sensation. The weight of your body. Your breath. Your feet on the floor.",
    hasInput:    false,
  },
  {
    key:         "funnel",
    label:       "FUNNEL",
    instruction: "Narrow further. Choose a single point — the rise of your chest, the tip of your nose. Each time your mind wanders, gently return.",
    hasInput:    false,
    hasRefocus:  true,
  },
  {
    key:         "breathe",
    label:       "BREATHE",
    instruction: "Take three slow breaths. In through the nose, out through the mouth. Let the exhale be longer than the inhale.",
    hasInput:    false,
  },
  {
    key:         "reintegrate",
    label:       "RE-INTEGRATE",
    instruction: "You are here. What is one small task you can bring this settled attention to?",
    hasInput:    true,
    inputLabel:  "One thing to focus on:",
    placeholder: "The task you'll return to...",
  },
];
const REFOCUS_NEEDED = 3;

export default function FocusAnchoringTemplate({ config = {}, onProgress, onComplete }) {
  const phases = config.phases?.length ? config.phases : DEFAULT_PHASES;

  const [phaseIndex, setPhaseIndex]   = useState(0);
  const [inputs, setInputs]           = useState({});
  const [redirectCount, setRedirectCount] = useState(0);
  const completedRef                  = useRef(false);

  const phase    = phases[phaseIndex];
  const isLast   = phaseIndex === phases.length - 1;
  const canAdvance = phase.hasRefocus ? redirectCount >= REFOCUS_NEEDED : true;

  const handleInput = (value) => {
    setInputs((prev) => ({ ...prev, [phase.key]: value }));
  };

  const handleNext = () => {
    if (!canAdvance) return;
    const next = phaseIndex + 1;
    if (next >= phases.length) {
      onProgress?.(1, "Complete");
      if (!completedRef.current) {
        completedRef.current = true;
        onComplete?.();
      }
      return;
    }
    setPhaseIndex(next);
    onProgress?.(next / phases.length, `Phase ${next + 1} of ${phases.length}`);
  };

  const handleRefocus = () => {
    setRedirectCount((c) => c + 1);
  };

  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-5 tw-w-full">
      {/* Phase step dots */}
      <div className="tw-flex tw-gap-2.5">
        {phases.map((p, i) => (
          <div
            key={p.key}
            className="tw-rounded-full tw-transition-all tw-duration-300"
            style={{
              width: 7, height: 7,
              background:
                i < phaseIndex  ? "rgba(203,90,60,0.5)" :
                i === phaseIndex ? "#5B3FD6" :
                "rgba(255,255,255,0.15)",
              transform: i === phaseIndex ? "scale(1.4)" : "scale(1)",
            }}
          />
        ))}
      </div>

      {/* Central focus dot */}
      <div
        className="tw-rounded-full tw-transition-all tw-duration-700"
        style={{
          width: 20, height: 20,
          background: canAdvance ? "rgba(203,90,60,0.8)" : "rgba(91,63,214,0.3)",
          boxShadow: canAdvance
            ? "0 0 30px rgba(203,90,60,0.5), 0 0 60px rgba(203,90,60,0.2)"
            : "0 0 20px rgba(91,63,214,0.2), 0 0 40px rgba(91,63,214,0.1)",
          animation: "at-focusPulse 3s ease-in-out infinite",
        }}
      />

      {/* Phase label */}
      <p className="tw-text-xs tw-font-bold tw-tracking-[3px] tw-uppercase"
        style={{ color: "rgba(160,140,255,0.9)" }}>
        {phase.label}
      </p>

      {/* Instruction */}
      <p className="tw-text-xl tw-font-light tw-text-center tw-max-w-[320px] tw-leading-relaxed"
        style={{ color: "rgba(255,255,255,0.85)" }}>
        {phase.instruction}
      </p>

      {/* Text input (phases 1 and 5) */}
      {phase.hasInput && (
        <input
          type="text"
          value={inputs[phase.key] || ""}
          onChange={(e) => handleInput(e.target.value)}
          placeholder={phase.placeholder}
          className="tw-rounded-xl tw-text-[15px] tw-outline-none tw-transition-all tw-duration-300"
          style={{
            width: "100%", maxWidth: 340,
            background: "rgba(255,255,255,0.07)",
            border: "1px solid rgba(255,255,255,0.12)",
            padding: "12px 16px",
            color: "rgba(255,255,255,0.85)",
            fontFamily: "inherit",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "rgba(160,140,255,0.5)";
            e.target.style.boxShadow   = "0 0 0 3px rgba(91,63,214,0.2)";
          }}
          onBlur={(e) => {
            e.target.style.borderColor = "rgba(255,255,255,0.12)";
            e.target.style.boxShadow   = "none";
          }}
        />
      )}

      {/* Refocus mechanic (phase 3 — funnel) */}
      {phase.hasRefocus && (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
          <button
            onClick={handleRefocus}
            disabled={redirectCount >= REFOCUS_NEEDED}
            className="tw-px-6 tw-py-2 tw-rounded-2xl tw-text-sm tw-transition-all hover:tw-scale-105"
            style={{
              border: "1px solid rgba(160,140,255,0.3)",
              background: "rgba(91,63,214,0.15)",
              color: "rgba(160,140,255,0.9)",
              cursor: redirectCount >= REFOCUS_NEEDED ? "default" : "pointer",
            }}
          >
            My mind wandered — refocus
          </button>
          <div className="tw-flex tw-items-center tw-gap-2">
            <span className="tw-text-xs" style={{ color: "rgba(255,255,255,0.4)" }}>
              Redirects:
            </span>
            <span className="tw-text-lg tw-font-light" style={{ color: "#CB5A3C" }}>
              {redirectCount} / {REFOCUS_NEEDED}
            </span>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="tw-flex tw-gap-4 tw-mt-1">
        {phaseIndex > 0 && (
          <button
            onClick={() => setPhaseIndex((i) => i - 1)}
            className="tw-px-5 tw-py-2 tw-rounded-2xl tw-text-sm tw-transition-all"
            style={{
              border: "1px solid rgba(255,255,255,0.2)",
              background: "transparent",
              color: "rgba(255,255,255,0.5)",
            }}
          >
            Back
          </button>
        )}
        <button
          onClick={handleNext}
          disabled={!canAdvance}
          className="tw-px-6 tw-py-2 tw-rounded-2xl tw-text-sm tw-font-semibold tw-transition-all hover:tw-scale-105"
          style={{
            background: canAdvance ? "#5B3FD6" : "rgba(255,255,255,0.05)",
            border: canAdvance ? "none" : "1px solid rgba(255,255,255,0.1)",
            color: canAdvance ? "white" : "rgba(255,255,255,0.25)",
            cursor: canAdvance ? "pointer" : "not-allowed",
          }}
        >
          {isLast ? "Complete" : "Continue"}
        </button>
      </div>
    </div>
  );
}
